<template>
  <div class="card" :class="cardClass">
    <div class="row gx-0" :class="videoPositionClass">
      <div
        class="col-12 col-md-6 vma-bg-image vma-lazy"
        :class="{ 'vma-lazy': bgImageLazy }"
        :data-background-image="dataBgImage"
        :style="bgImage">
        <div class="d-flex justify-content-center align-items-center h-100">
          <button
            class="btn position-relative"
            aria-label="play video"
            @click="handleModalOpen($event)">
            <!-- play icon -->
            <img
              :data-src="playBtn"
              alt="play video"
              class="play-btn vma-lazy" />
          </button>
        </div>
      </div>
      <div class="col-12 col-md-6">
        <div class="d-flex" :class="videoPositionClass">
          <div
            v-if="displayHighlight"
            class="h-auto d-none d-md-block"
            :style="highlightStyle"></div>
          <div
            class="card-body py-space-sm px-space-xs px-md-space-sm p-lg-space-xl">
            <!-- title -->
            <component
              v-if="displayTitle"
              :is="titleLevel"
              class="mb-space-sm"
              :class="titleClass">
              {{ title }}
            </component>

            <!-- text slot -->
            <div>
              <slot></slot>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- video modal -->
    <rds-modal
      :visible="showModal"
      centered
      exterior-close-btn
      size="xl"
      body-class="p-0"
      @hidden="handleModalClose($event)"
      bg-variant="transparent">
      <div class="ratio ratio-16x9">
        <iframe
          title="Video"
          class="w-100 h-100"
          :src="videoSourceUrl"
          ref="video"></iframe>
      </div>
    </rds-modal>
  </div>
</template>

<script setup lang="ts">
import { RdsModal } from "@rds-vue-ui/rds-modal";
import lozad from "lozad";
import { computed, onMounted, ref } from "vue";
import playBtn from "./assets/play-button.svg?url";

interface Props {
  videoSource: string;
  videoPosition?: "right" | "left";
  autoplayVideo?: boolean;
  displayBorder?: boolean;
  borderVariant?: string;
  bgImageSource: string;
  bgImageLazy?: boolean;
  displayHighlight?: boolean;
  highlightColor?: string;
  highlightWidth?: number;
  displayTitle?: boolean;
  title?: string;
  titleLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  titleSize?: string;
  titleVariant?: string;
}

const props = withDefaults(defineProps<Props>(), {
  videoPosition: "left",
  autoplayVideo: false,
  displayBorder: true,
  borderVariant: "light-4",
  bgImageLazy: true,
  displayHighlight: true,
  highlightColor: "var( --rds-secondary, #ffc627)",
  highlightWidth: 8,
  displayTitle: true,
  title: "Lorem ipsum",
  titleLevel: "h2",
  titleSize: "medium",
  titleVariant: "dark-3",
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when the modal is closed.
   */
  (e: "modalClose", event: Event): void;
  /**
   *  Emitted when the modal is opened.
   */
  (e: "modalOpen", event: Event): void;
}>();

const showModal = ref(false);
const video = ref<HTMLIFrameElement | null>();

const videoSourceUrl = computed(() => {
  if (props.autoplayVideo) {
    return `${props.videoSource}?&autoplay=1`;
  }

  return props.videoSource;
});

const videoPositionClass = computed(() => {
  return props.videoPosition == "left" ? `flex-row` : `flex-row-reverse`;
});

const bgImage = computed(() => {
  if (!props.bgImageLazy) {
    return { "background-image": `url('${props.bgImageSource}')` };
  } else {
    return "";
  }
});

const cardClass = computed(() => {
  if (props.displayBorder) {
    return `border border-${props.borderVariant}`;
  }

  return "border-0";
});

const highlightStyle = computed(() => {
  if (props.displayHighlight) {
    return {
      "border-left": `${props.highlightWidth}px solid ${props.highlightColor} !important`,
    };
  }

  return "";
});

const titleClass = computed(() => {
  return `${props.titleLevel}-${props.titleSize} text-${props.titleVariant}`;
});

const dataBgImage = computed(() => {
  return props.bgImageLazy ? props.bgImageSource : null;
});

const handleModalOpen = (event: Event) => {
  showModal.value = true;
  emits("modalOpen", event);
};

const handleModalClose = (event: Event) => {
  video.value!.src = video.value!.src;
  showModal.value = false;
  emits("modalClose", event);
};

onMounted(() => {
  const observer = lozad(".vma-lazy");
  observer.observe();
});
</script>

<style lang="scss" scoped>
.vma-bg-image {
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center top;
  height: 200px;
}

.svm-modal-close-btn {
  right: 0px;
  top: -40px;
}

.play-btn {
  height: 96px;
  width: 96px;
  opacity: 0.7;
}

.play-btn:hover {
  transform: scale(1.05);
  opacity: 1;
  transition: 0.1s ease-out;
}

/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {
  .vma-bg-image {
    height: auto;
  }
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .play-btn {
    height: 128px;
    width: 128px;
  }
}
</style>
